import React, { createContext, useState, useEffect } from 'react';
import { createUser } from '../lib/models/user';
import { useAmplitude } from './shared/use-amplitude';
import { useRouting } from './shared/use-routing';
import { signIn } from '../lib/use-cases/sign-in';
import { signOut } from '../lib/use-cases/sign-out';
import { deleteAccount } from '../lib/use-cases/delete-account';
import { logEvent } from '../lib/shared/event-logger';
import { EVENT_TYPES } from '../lib/shared/event-types';
import { pagePathnames } from './pages/page-routes';
import { getUser } from '../lib/use-cases/get-user';
import { getCurrentSubscription } from '../lib/use-cases/get-current-subscription';
import { signInBySubscription } from '../lib/use-cases/sign-in-by-subscription';

const AppContext = createContext();

const AppProvider = ({children}) => {
    const {navigate, params} = useRouting();
    const {resetFromAmplitude} = useAmplitude();
    const [isLoading, setIsLoading] = useState(Boolean(params.subscriptionId) ? false : true);
    const [userData, setUserData] = useState(createUser({}));

    const init = async () => {
        try {
            await getUserData();
        } catch(error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!params.subscriptionId) {
            init();
        }
    }, []);

    const getUserData = async () => {
        try {
            const newUserData = await getUser();
            const subscriptionData = await getCurrentSubscription();

            setUserData({...newUserData, isActiveSubscription: subscriptionData.isActive});
            logEvent(EVENT_TYPES.USER_ID, newUserData.id);
        } catch (error) {   
            throw error;
        }
    };

    const setInitialUserData = () => setUserData(createUser({isAuthenticated: true}));

    const signInUser = async token => {
        try {
            await signIn(token);

            setInitialUserData();
            getUserData();
            navigate(pagePathnames.main);
        } catch (error) {
            throw error;
        }
    };

    const signInUserBySubscription = async subscriptionId => {
        try {
            await signInBySubscription(subscriptionId);
            await getUserData();
        } catch (error) {
            throw error;
        }
    };

    const signOutUser = async () => {
        signOut();
        setUserData({});
        resetFromAmplitude();
        navigate(pagePathnames.start);
    };

    const value = {
        userData,
        isLoading,
        signIn: signInUser,
        signInBySubscription: signInUserBySubscription,
        signOut: signOutUser,
        deleteAccount
    };

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};

export {
    AppProvider,
    AppContext
}
